'use client';

import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import { GeoAreaSelect } from '@/components/lookups/GeoAreaSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ActivationSettings } from '@sizzlenz/shared';
import { ACTIVATION_ROLE_OPTIONS, INITIATIVE_TAG_OPTIONS, ACTIVATION_ENVIRONMENT_OPTIONS, ACTIVATION_AUDIENCE_OPTIONS, ACTIVATION_CAPABILITY_OPTIONS, ACTIVATION_TERMS_OPTIONS, ACTIVATION_REQUIREMENT_OPTIONS, ACTIVATION_AVAILABILITY_OPTIONS, ACTIVATION_PROMOTION_OPTIONS, PRODUCT_CATEGORY_OPTIONS } from '@sizzlenz/shared';
const DEFAULT_VALUES: ActivationSettings = {
  roles: [],
  activities: [],
  environment: '',
  audienceProfile: [],
  capabilities: [],
  terms: [],
  requirements: [],
  availability: [],
  promotionSupport: [],
  preferredBrandCategories: [],
  locations: []
};
function toOptions(items: readonly {
  key: string;
  label: string;
}[]) {
  return items.map(i => ({
    id: i.key,
    label: i.label
  }));
}
interface ActivationSettingsFormProps {
  initialValue?: Partial<ActivationSettings>;
  onChange?: (value: ActivationSettings) => void;
}
export function ActivationSettingsForm({
  initialValue,
  onChange
}: ActivationSettingsFormProps) {
  const {
    control,
    watch
  } = useForm<ActivationSettings>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialValue
    }
  });
  const activityOptions = useMemo(() => {
    return toOptions(INITIATIVE_TAG_OPTIONS.filter(a => a.scopes?.includes('activity')));
  }, [INITIATIVE_TAG_OPTIONS]);
  // Notify parent on every change
  useEffect(() => {
    const subscription = watch(value => {
      onChange?.(value as ActivationSettings);
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange]);
  return <div className="space-y-6">
      {/* Row 1: Roles & Activities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Activation Roles</Label>
          <Controller control={control} name="roles" render={({
          field
        }) => <MultiSelectDropdown label={null} placeholder="Select roles..." options={toOptions(ACTIVATION_ROLE_OPTIONS)} selectedIds={field.value} onSelectionChange={field.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
        <div className="space-y-2">
          <Label>Activities</Label>
          <Controller control={control} name="activities" render={({
          field: field_0
        }) => <MultiSelectDropdown label={null} placeholder="Select activities..." options={activityOptions} selectedIds={field_0.value} onSelectionChange={field_0.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
      </div>

      {/* Row 2: Environment & Audience */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Environment</Label>
          <Controller control={control} name="environment" render={({
          field: field_1
        }) => <Select value={field_1.value} onValueChange={v => field_1.onChange(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select environment..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>No selection</SelectItem>
                  {ACTIVATION_ENVIRONMENT_OPTIONS.map(opt => <SelectItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>} />
        </div>
        <div className="space-y-2">
          <Label>Audience Profile</Label>
          <Controller control={control} name="audienceProfile" render={({
          field: field_2
        }) => <MultiSelectDropdown label={null} placeholder="Select audience..." options={toOptions(ACTIVATION_AUDIENCE_OPTIONS)} selectedIds={field_2.value} onSelectionChange={field_2.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
      </div>

      {/* Row 3: Capabilities & Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Facilities / Equipment</Label>
          <Controller control={control} name="capabilities" render={({
          field: field_3
        }) => <MultiSelectDropdown label={null} placeholder="Select facilities..." options={toOptions(ACTIVATION_CAPABILITY_OPTIONS)} selectedIds={field_3.value} onSelectionChange={field_3.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
        <div className="space-y-2">
          <Label>Commercial Terms</Label>
          <Controller control={control} name="terms" render={({
          field: field_4
        }) => <MultiSelectDropdown label={null} placeholder="Select terms..." options={toOptions(ACTIVATION_TERMS_OPTIONS)} selectedIds={field_4.value} onSelectionChange={field_4.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
      </div>

      {/* Row 4: Requirements & Availability */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Requirements</Label>
          <Controller control={control} name="requirements" render={({
          field: field_5
        }) => <MultiSelectDropdown label={null} placeholder="Select requirements..." options={toOptions(ACTIVATION_REQUIREMENT_OPTIONS)} selectedIds={field_5.value} onSelectionChange={field_5.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
        <div className="space-y-2">
          <Label>Availability</Label>
          <Controller control={control} name="availability" render={({
          field: field_6
        }) => <MultiSelectDropdown label={null} placeholder="Select availability..." options={toOptions(ACTIVATION_AVAILABILITY_OPTIONS)} selectedIds={field_6.value} onSelectionChange={field_6.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
      </div>

      {/* Row 5: Promotion Support & Brand Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Promotion Support</Label>
          <Controller control={control} name="promotionSupport" render={({
          field: field_7
        }) => <MultiSelectDropdown label={null} placeholder="Select promotion support..." options={toOptions(ACTIVATION_PROMOTION_OPTIONS)} selectedIds={field_7.value} onSelectionChange={field_7.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
        <div className="space-y-2">
          <Label>Preferred Product Categories</Label>
          <Controller control={control} name="preferredBrandCategories" render={({
          field: field_8
        }) => <MultiSelectDropdown label={null} placeholder="Select brand categories..." options={toOptions(PRODUCT_CATEGORY_OPTIONS)} selectedIds={field_8.value} onSelectionChange={field_8.onChange} showChips showSummaryInTrigger showBulkActions={false} />} />
        </div>
      </div>

      {/* Row 6: Locations */}
      <div className="space-y-2">
        <Label>Locations</Label>
        <Controller control={control} name="locations" render={({
        field: field_9
      }) => <GeoAreaSelect label={null} selectedAreas={field_9.value || []} onSelectionChange={field_9.onChange} placeholder="Select locations..." showChips showSummaryInTrigger showBulkActions={false} />} />
      </div>

      {/* Row 7: Capacity fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Min Attendees</Label>
          <Controller control={control} name="minAttendees" render={({
          field: field_10
        }) => <Input type="number" placeholder="e.g. 50" value={field_10.value ?? ''} onChange={e => field_10.onChange(e.target.value ? Number(e.target.value) : undefined)} />} />
        </div>
        <div className="space-y-2">
          <Label>Max Attendees</Label>
          <Controller control={control} name="maxAttendees" render={({
          field: field_11
        }) => <Input type="number" placeholder="e.g. 50" value={field_11.value ?? ''} onChange={e_0 => field_11.onChange(e_0.target.value ? Number(e_0.target.value) : undefined)} />} />
        </div>
        <div className="space-y-2">
          <Label>Typical Duration (hrs)</Label>
          <Controller control={control} name="typicalDuration" render={({
          field: field_12
        }) => <Input type="number" placeholder="e.g. 4" value={field_12.value ?? ''} onChange={e_1 => field_12.onChange(e_1.target.value ? Number(e_1.target.value) : undefined)} />} />
        </div>
        <div className="space-y-2">
          <Label>Space Size (m²)</Label>
          <Controller control={control} name="spaceSizeM2" render={({
          field: field_13
        }) => <Input type="number" placeholder="e.g. 20" value={field_13.value ?? ''} onChange={e_2 => field_13.onChange(e_2.target.value ? Number(e_2.target.value) : undefined)} />} />
        </div>
        <div className="space-y-2">
          <Label>Daily Foot Traffic</Label>
          <Controller control={control} name="approxDailyFootTraffic" render={({
          field: field_14
        }) => <Input type="number" placeholder="e.g. 500" value={field_14.value ?? ''} onChange={e_3 => field_14.onChange(e_3.target.value ? Number(e_3.target.value) : undefined)} />} />
        </div>
      </div>
    </div>;
}